import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import useOfflineStatus from "@rehooks/online-status";
import { answerEndpoint } from "../actions/response";
import useInterval from "@use-it/interval";
import useLocalStorage from "@illinois/react-use-local-storage";
import { SYNC_AUTH_ERROR, RESPONSE_ENDPOINT } from "../actions/sync";

export const REMOVE_FROM_ANSWERS_TO_SYNC = "REMOVE_FROM_ANSWERS_TO_SYNC";

class AuthenticationError extends Error {}
class ServerError extends Error {}

const createPendingItem = id => ({
  id,
  time: new Date().getTime()
});

const containsId = (list, id) => list.find(item => item.id === id);
const removeItemFromList = (list, item) => list.filter((i) => i !== item);

const syncAnswer = async (answer, id, responseId, token) => {
  return fetch(answerEndpoint(responseId), {
    method: "POST",
    compress: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: token
    },
    body: JSON.stringify({
      ...answer,
      question: id
    })
  }).then(res => {
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new AuthenticationError("Not authorized");
      } else if (res.status === 500) {
        throw new ServerError("Server error");
      } else {
        throw new Error("Unknown network error");
      }
    } else {
      return;
    }
  });
};

const putResponse = async (response, token) => {
  let url = `${RESPONSE_ENDPOINT}/${response.metadata.id}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': token
    },
    body: JSON.stringify(response)
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      throw new AuthenticationError("Not authorized");
    } else if (res.status === 500) {
      throw new ServerError("Server error");
    } else {
      throw new Error("Unknown network error");
    }
  }
  return;
}

const useBackgroundSync = () => {
  const online = useOfflineStatus();
  const [error, setError] = useState(null);
  const changedAnswers = useSelector(state => state.sync.answersToSync);
  const dispatch = useDispatch();
  const [pendingChanges, setPendingChanges] = useState([]);
  const [syncing, setSyncing] = useState([]);
  const [failedToSync, setFailedToSync] = useState([]);
  const token = useSelector(state => state.auth.token);
  const [ shouldDisplayDebugUI, setDebugUIVisible] = useContext(BackgroundSyncUIContext);
  const [ forceSaveInProgress, setForceSaveInProgress ] = useState(false);
  const [ forceSaveError, setForceSaveError ] = useState(null);

  const response = useSelector(state => {
    const responseID = state.responses.responseID;
    return state.responses.items.find(r => r.metadata.id === responseID);
  });

  const sync = () => {
    if (!online) {
      setDebugUIVisible(true);
    }
    if (online && pendingChanges.length > 0) {
      for (const item of pendingChanges) {
        syncAnswer(
          response.answers[item.id],
          item.id,
          response.metadata.id,
          token
        )
          .then(() => {
            setSyncing((prevItems) => removeItemFromList(prevItems, item));
          })
          .catch(e => {
            if (e instanceof AuthenticationError) {
              dispatch({type: SYNC_AUTH_ERROR});
            }
            setDebugUIVisible(true);
            setSyncing(prevItems => removeItemFromList(prevItems, item));
            setFailedToSync(prevItems => [...prevItems, item]);
            console.error(e);
            setError(`Problem saving answer #${item.id}. ${e.message}`);
          });
      }
      setPendingChanges([]);
      setSyncing((items) => [...items, ...pendingChanges]);
    }
  };

  useInterval(sync, 1000);

  useInterval(() => {
    if (failedToSync.length) {
      setError(null);
      setPendingChanges((items) => [...items, ...failedToSync]);
      setFailedToSync([]);
    }
  }, 10000);

  if (changedAnswers.length > 0) {
    const removed = [];
    for (const id of changedAnswers) {
      const answer = response.answers[id];
      // if the answer doesn't have a value, don't send it because it will
      // fail at the server
      if (answer.value) {
        removed.push(id);
        if (!containsId(pendingChanges, id) && !containsId(failedToSync, id)) {
          setPendingChanges(prevItems => [...prevItems, createPendingItem(id)]);
        }
      }
    }
    if (removed.length) {
      dispatch({
        type: REMOVE_FROM_ANSWERS_TO_SYNC,
        ids: [...removed, ...pendingChanges.map((item) => item.id)],
        responseId: response.metadata.id
      });
    }
  }

  return {
    online,
    failedChanges: failedToSync.length,
    pendingChanges: pendingChanges.length,
    syncingChanges: syncing.length,
    error,
    forceSave: async () => {
      setForceSaveInProgress(true);
      try {
        await putResponse(response, token);
        setForceSaveInProgress(false);
        setFailedToSync([]);
        setError(null);
        setPendingChanges([]);
        setForceSaveError(null);
      } catch(e) {
        setForceSaveInProgress(false);
        setForceSaveError(e.toString());
      }
    },
    forceSaveInProgress,
    forceSaveError,
    shouldDisplayDebugUI,
    setDebugUIVisible,
  };
};

export default useBackgroundSync;

export const BackgroundSyncUIContext = React.createContext({ showBar: false });

export const BackgroundSyncUIContextProvider = ({children}) => {
  const [visible, setVisible] = useLocalStorage("background-sync-ui-visible", false);
  return <BackgroundSyncUIContext.Provider value={[visible, setVisible]}>{children}</BackgroundSyncUIContext.Provider>
}
